import type { ProductForm } from './allowableStressValues'

/**
 * ASME B31.3-2022 Table A-1 — raw data catalog (Low & Intermediate Alloy Steel,
 * lines 1–121: Pipes / Plates / Forgings & Fittings / Castings).
 *
 * PURE DATA — no runtime imports. It is transcribed from the source PDF via OCR and
 * machine-validated in allowableStressValues.test.ts (ambient rule
 * `S(≤100) = min(tensile/3, ⅔·yield)`, monotonic S(T), value-count vs max temp).
 * `allowableStressValues.ts` maps these rows into the runtime `AllowableStressRec`
 * (GeckoMeasurement) shape. TODO(FFS): add the carbon-steel & stainless sections.
 */
export interface RawRow {
  line: number
  form: ProductForm
  material: string
  spec: string
  grade: string | null
  cls: string | null
  uns: string | null
  pNo: string
  minTempF: number
  maxTempF: number
  tensile: number
  yield: number | null
  /** Allowable stress S (ksi), ambient (≤100 °F) → max temp; entries beyond max temp are omitted. */
  s: ReadonlyArray<number | null>
}

export const RAW: readonly RawRow[] = [
  // ---- Pipes (1–56) ----
  {
    line: 1,
    form: 'Pipes',
    material: '½Cr–½Mo',
    spec: 'A335',
    grade: 'P2',
    cls: null,
    uns: 'K11547',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 55,
    yield: 30,
    s: [
      18.3, 18.3, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.9,
      9.2, 5.9,
    ],
  },
  {
    line: 2,
    form: 'Pipes',
    material: '½Cr–½Mo (A387 Gr.2 Cl.1)',
    spec: 'A691',
    grade: '½CR',
    cls: null,
    uns: 'K12143',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 55,
    yield: 33,
    s: [
      18.3, 18.3, 18.3, 18.3, 18.3, 18.0, 17.7, 17.3, 16.9, 16.4, 15.9, 14.3,
      9.2, 5.9,
    ],
  },
  {
    line: 3,
    form: 'Pipes',
    material: 'C–½Mo',
    spec: 'A335',
    grade: 'P1',
    cls: null,
    uns: 'K11522',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 55,
    yield: 30,
    s: [
      18.3, 18.3, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 4,
    form: 'Pipes',
    material: 'C–½Mo',
    spec: 'A369',
    grade: 'FP1',
    cls: null,
    uns: 'K11522',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 55,
    yield: 30,
    s: [
      18.3, 18.3, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 5,
    form: 'Pipes',
    material: '½Cr–½Mo',
    spec: 'A369',
    grade: 'FP2',
    cls: null,
    uns: 'K11547',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 55,
    yield: 30,
    s: [
      18.3, 18.3, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.9,
      9.2, 5.9, 4.1, 2.5,
    ],
  },
  {
    line: 6,
    form: 'Pipes',
    material: '1Cr–½Mo (A387 Gr.12 Cl.1)',
    spec: 'A691',
    grade: '1CR',
    cls: null,
    uns: 'K11757',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 55,
    yield: 33,
    s: [
      18.3, 18.3, 17.6, 17.6, 17.2, 16.8, 16.5, 16.3, 16.0, 15.7, 15.4, 15.0,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 7,
    form: 'Pipes',
    material: '½Cr–½Mo',
    spec: 'A426',
    grade: 'CP2',
    cls: null,
    uns: 'J11547',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.8, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.9,
      9.2, 5.9, 4.0, 2.4,
    ],
  },
  {
    line: 8,
    form: 'Pipes',
    material: '1½Si–½Mo',
    spec: 'A335',
    grade: 'P15',
    cls: null,
    uns: 'K11578',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.8, 18.2, 17.7, 17.3, 16.8, 16.6, 16.3, 15.9, 15.4, 13.8, 12.5,
      10.0, 6.3, 4.0, 2.4,
    ],
  },
  {
    line: 9,
    form: 'Pipes',
    material: 'C–½Mo–Si',
    spec: 'A426',
    grade: 'CP15',
    cls: null,
    uns: 'J11522',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.8, 18.2, 17.7, 17.3, 16.8, 16.6, 16.3, 15.9, 15.4, 13.8, 12.5,
      10.0, 6.3, 4.0, 2.4,
    ],
  },
  {
    line: 10,
    form: 'Pipes',
    material: '1Cr–½Mo',
    spec: 'A426',
    grade: 'CP12',
    cls: null,
    uns: 'J11562',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.1, 17.0, 16.2, 15.7, 15.2, 15.0, 14.8, 14.6, 14.3, 14.0, 13.6,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 11,
    form: 'Pipes',
    material: '5Cr–1½Si–½Mo',
    spec: 'A426',
    grade: 'CP5b',
    cls: null,
    uns: 'J51545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 12,
    form: 'Pipes',
    material: '3Cr–Mo',
    spec: 'A426',
    grade: 'CP21',
    cls: null,
    uns: 'J31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: null,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 16.0, 12.0,
      9.0, 7.0, 5.5, 4.0, 2.7, 1.5,
    ],
  },
  {
    line: 13,
    form: 'Pipes',
    material: '¾Cr–¾Ni–Cu–Al',
    spec: 'A333',
    grade: '4',
    cls: null,
    uns: 'K11267',
    pNo: '4',
    minTempF: -150,
    maxTempF: 650,
    tensile: 60,
    yield: 35,
    s: [20.0, 19.1, 18.2, 17.3, 16.4, 15.5, 15.0],
  },
  {
    line: 14,
    form: 'Pipes',
    material: '2Cr–½Mo',
    spec: 'A369',
    grade: 'FP3b',
    cls: null,
    uns: 'K21509',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.5, 12.5,
      10.0, 6.2, 4.2, 2.6, 1.4, 1.0,
    ],
  },
  {
    line: 15,
    form: 'Pipes',
    material: '1Cr–½Mo',
    spec: 'A335',
    grade: 'P12',
    cls: null,
    uns: 'K11562',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 32,
    s: [
      20.0, 19.3, 18.1, 17.3, 16.7, 16.3, 16.0, 15.8, 15.5, 15.3, 14.9, 14.5,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 16,
    form: 'Pipes',
    material: '1Cr–½Mo',
    spec: 'A369',
    grade: 'FP12',
    cls: null,
    uns: 'K11562',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 32,
    s: [
      20.0, 19.3, 18.1, 17.3, 16.7, 16.3, 16.0, 15.8, 15.5, 15.3, 14.9, 14.5,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 17,
    form: 'Pipes',
    material: '1¼Cr–½Mo–Si',
    spec: 'A335',
    grade: 'P11',
    cls: null,
    uns: 'K11597',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.5, 17.6, 16.8, 16.2, 15.7, 15.4, 15.1, 14.8, 14.4, 14.0, 13.6,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 18,
    form: 'Pipes',
    material: '1¼Cr–½Mo–Si',
    spec: 'A369',
    grade: 'FP11',
    cls: null,
    uns: 'K11597',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.5, 17.6, 16.8, 16.2, 15.7, 15.4, 15.1, 14.8, 14.4, 14.0, 13.6,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 19,
    form: 'Pipes',
    material: '1¼Cr–½Mo–Si (A387 Gr.11 Cl.1)',
    spec: 'A691',
    grade: '1¼CR',
    cls: null,
    uns: 'K11789',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 35,
    s: [
      20.0, 20.0, 20.0, 19.6, 18.9, 18.3, 18.0, 17.6, 17.2, 16.8, 16.4, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 20,
    form: 'Pipes',
    material: '5Cr–½Mo (A387 Gr.5 Cl.1)',
    spec: 'A691',
    grade: '5CR',
    cls: null,
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 21,
    form: 'Pipes',
    material: '5Cr–½Mo',
    spec: 'A335',
    grade: 'P5',
    cls: null,
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 22,
    form: 'Pipes',
    material: '5Cr–½Mo–Si',
    spec: 'A335',
    grade: 'P5b',
    cls: null,
    uns: 'K51545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 23,
    form: 'Pipes',
    material: '5Cr–½Mo–Ti',
    spec: 'A335',
    grade: 'P5c',
    cls: null,
    uns: 'K41245',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 24,
    form: 'Pipes',
    material: '5Cr–½Mo',
    spec: 'A369',
    grade: 'FP5',
    cls: null,
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 25,
    form: 'Pipes',
    material: '9Cr–1Mo',
    spec: 'A335',
    grade: 'P9',
    cls: null,
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.8, 14.1,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 26,
    form: 'Pipes',
    material: '9Cr–1Mo',
    spec: 'A369',
    grade: 'FP9',
    cls: null,
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.8, 14.1,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 27,
    form: 'Pipes',
    material: '9Cr–1Mo (A387 Gr.9 Cl.1)',
    spec: 'A691',
    grade: '9CR',
    cls: null,
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.8, 14.1,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 28,
    form: 'Pipes',
    material: '3Cr–1Mo',
    spec: 'A335',
    grade: 'P21',
    cls: null,
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 16.0, 12.0,
      9.0, 7.0, 5.5, 4.0, 2.7, 1.5,
    ],
  },
  {
    line: 29,
    form: 'Pipes',
    material: '3Cr–1Mo',
    spec: 'A369',
    grade: 'FP21',
    cls: null,
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 16.0, 12.0,
      9.0, 7.0, 5.5, 4.0, 2.7, 1.5,
    ],
  },
  {
    line: 30,
    form: 'Pipes',
    material: '3Cr–1Mo',
    spec: 'A691',
    grade: '3CR',
    cls: null,
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.5, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 16.0, 12.0,
      9.0, 7.0, 5.5, 4.0, 2.7, 1.5,
    ],
  },
  {
    line: 31,
    form: 'Pipes',
    material: '2¼Cr–1Mo (A387 Gr.22 Cl.1)',
    spec: 'A691',
    grade: '2¼CR',
    cls: null,
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 32,
    form: 'Pipes',
    material: '2¼Cr–1Mo',
    spec: 'A369',
    grade: 'FP22',
    cls: null,
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 33,
    form: 'Pipes',
    material: '2¼Cr–1Mo',
    spec: 'A335',
    grade: 'P22',
    cls: null,
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 34,
    form: 'Pipes',
    material: '2Ni–1Cu',
    spec: 'A333',
    grade: '9',
    cls: null,
    uns: 'K22035',
    pNo: '9A',
    minTempF: -100,
    maxTempF: 100,
    tensile: 63,
    yield: 46,
    s: [21.0],
  },
  {
    line: 35,
    form: 'Pipes',
    material: '2Ni–1Cu',
    spec: 'A334',
    grade: '9',
    cls: null,
    uns: 'K22035',
    pNo: '9A',
    minTempF: -100,
    maxTempF: 100,
    tensile: 63,
    yield: 46,
    s: [21.0],
  },
  {
    line: 36,
    form: 'Pipes',
    material: '2½Ni',
    spec: 'A333',
    grade: '7',
    cls: null,
    uns: 'K21903',
    pNo: '9A',
    minTempF: -100,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.4, 20.6, 19.9, 18.9, 17.5, 16.7, 15.7, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5, 1.6, 1.0,
    ],
  },
  {
    line: 37,
    form: 'Pipes',
    material: '2½Ni',
    spec: 'A334',
    grade: '7',
    cls: null,
    uns: 'K21903',
    pNo: '9A',
    minTempF: -100,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.4, 20.6, 19.9, 18.9, 17.5, 16.7, 15.7, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5, 1.6, 1.0,
    ],
  },
  {
    line: 38,
    form: 'Pipes',
    material: '3½Ni',
    spec: 'A333',
    grade: '3',
    cls: null,
    uns: 'K31918',
    pNo: '9B',
    minTempF: -150,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.4, 20.6, 19.9, 18.9, 17.5, 16.7, 15.7, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5, 1.6, 1.0,
    ],
  },
  {
    line: 39,
    form: 'Pipes',
    material: '3½Ni',
    spec: 'A334',
    grade: '3',
    cls: null,
    uns: 'K31918',
    pNo: '9B',
    minTempF: -150,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.4, 20.6, 19.9, 18.9, 17.5, 16.7, 15.7, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5, 1.6, 1.0,
    ],
  },
  {
    line: 40,
    form: 'Pipes',
    material: 'C–½Mo',
    spec: 'A426',
    grade: 'CP1',
    cls: null,
    uns: 'J12521',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.7, 21.0, 20.3, 19.7, 19.1, 18.7, 18.4, 17.9, 17.4, 16.9, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 41,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.A)',
    spec: 'A672',
    grade: 'L65',
    cls: null,
    uns: 'K11820',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 65,
    yield: 37,
    s: [
      21.7, 21.7, 21.7, 21.5, 20.8, 20.2, 19.8, 19.4, 19.0, 18.4, 17.9, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 42,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.A)',
    spec: 'A691',
    grade: 'CM-65',
    cls: null,
    uns: 'K11820',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 65,
    yield: 37,
    s: [
      21.7, 21.7, 21.7, 21.5, 20.8, 20.2, 19.8, 19.4, 19.0, 18.4, 17.9, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 43,
    form: 'Pipes',
    material: '2¼Ni (A203 Gr.B)',
    spec: 'A671',
    grade: 'CFB70',
    cls: null,
    uns: 'K22103',
    pNo: '9A',
    minTempF: -20,
    maxTempF: 100,
    tensile: 70,
    yield: 40,
    s: [23.3],
  },
  {
    line: 44,
    form: 'Pipes',
    material: '3½Ni (A203 Gr.E)',
    spec: 'A671',
    grade: 'CFE70',
    cls: null,
    uns: 'K32018',
    pNo: '9B',
    minTempF: -20,
    maxTempF: 100,
    tensile: 70,
    yield: 40,
    s: [23.3],
  },
  {
    line: 45,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.B)',
    spec: 'A672',
    grade: 'L70',
    cls: null,
    uns: 'K12020',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.2, 22.5, 21.8, 21.4, 21.0, 20.5, 19.9, 19.3, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 46,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.B)',
    spec: 'A691',
    grade: 'CM-70',
    cls: null,
    uns: 'K12020',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.2, 22.5, 21.8, 21.4, 21.0, 20.5, 19.9, 19.3, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 47,
    form: 'Pipes',
    material: '1¼Cr–½Mo',
    spec: 'A426',
    grade: 'CP11',
    cls: null,
    uns: 'J12072',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.5, 21.7, 20.9, 20.5, 20.1, 19.7, 19.2, 18.7, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 48,
    form: 'Pipes',
    material: '2¼Cr–1Mo',
    spec: 'A426',
    grade: 'CP22',
    cls: null,
    uns: 'J21890',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 21.9, 15.8,
      11.4, 7.8, 5.1, 3.2, 2.0, 1.2,
    ],
  },
  {
    line: 49,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.C)',
    spec: 'A672',
    grade: 'L75',
    cls: null,
    uns: 'K12320',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 75,
    yield: 43,
    s: [
      25.0, 25.0, 25.0, 25.0, 24.2, 23.4, 23.0, 22.6, 22.0, 21.4, 20.7, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 50,
    form: 'Pipes',
    material: 'C–½Mo (A204 Gr.C)',
    spec: 'A691',
    grade: 'CM-75',
    cls: null,
    uns: 'K12320',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 75,
    yield: 43,
    s: [
      25.0, 25.0, 25.0, 25.0, 24.2, 23.4, 23.0, 22.6, 22.0, 21.4, 20.7, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 51,
    form: 'Pipes',
    material: '9Cr–1Mo–V',
    spec: 'A335',
    grade: 'P91',
    cls: null,
    uns: 'K90901',
    pNo: '15E',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 60,
    s: [
      28.3, 28.3, 28.3, 28.2, 28.1, 27.7, 27.3, 26.7, 25.9, 24.9, 23.7, 22.3,
      20.7, 18.0, 14.0, 10.3, 7.0, 4.3,
    ],
  },
  {
    line: 52,
    form: 'Pipes',
    material: '9Cr–1Mo–V',
    spec: 'A691',
    grade: '91',
    cls: null,
    uns: 'K90901',
    pNo: '15E',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 60,
    s: [
      28.3, 28.3, 28.3, 28.2, 28.1, 27.7, 27.3, 26.7, 25.9, 24.9, 23.7, 22.3,
      20.7, 18.0, 14.0, 10.3, 7.0, 4.3,
    ],
  },
  {
    line: 53,
    form: 'Pipes',
    material: '5Cr–½Mo',
    spec: 'A426',
    grade: 'CP5',
    cls: null,
    uns: 'J42045',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 90,
    yield: 60,
    s: [
      30.0, 29.9, 29.1, 28.8, 28.7, 28.3, 27.9, 27.3, 26.5, 25.5, 24.2, 16.4,
      11.0, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 54,
    form: 'Pipes',
    material: '9Cr–1Mo',
    spec: 'A426',
    grade: 'CP9',
    cls: null,
    uns: 'J82090',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 90,
    yield: 60,
    s: [
      30.0, 29.9, 29.1, 28.8, 28.7, 28.3, 27.9, 27.3, 26.5, 25.5, 24.2, 16.4,
      11.0, 7.4, 5.0, 3.8, 2.2, 1.5,
    ],
  },
  {
    line: 55,
    form: 'Pipes',
    material: '9Ni',
    spec: 'A333',
    grade: '8',
    cls: null,
    uns: 'K81340',
    pNo: '11A',
    minTempF: -320,
    maxTempF: 200,
    tensile: 100,
    yield: 75,
    s: [33.3, 33.3],
  },
  {
    line: 56,
    form: 'Pipes',
    material: '9Ni',
    spec: 'A334',
    grade: '8',
    cls: null,
    uns: 'K81340',
    pNo: '11A',
    minTempF: -320,
    maxTempF: 200,
    tensile: 100,
    yield: 75,
    s: [33.3, 33.3],
  },
  // ---- Plates (57–85) ----
  {
    line: 57,
    form: 'Plates',
    material: '½Cr–½Mo',
    spec: 'A387',
    grade: '2',
    cls: '1',
    uns: 'K12143',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 55,
    yield: 33,
    s: [
      18.3, 18.3, 18.3, 18.3, 18.3, 18.0, 17.7, 17.3, 16.9, 16.4, 15.9, 14.3,
      9.2, 5.9,
    ],
  },
  {
    line: 58,
    form: 'Plates',
    material: '1Cr–½Mo',
    spec: 'A387',
    grade: '12',
    cls: '1',
    uns: 'K11757',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 55,
    yield: 33,
    s: [
      18.3, 18.0, 17.6, 17.6, 17.2, 16.8, 16.5, 16.3, 16.0, 15.7, 15.4, 15.0,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 59,
    form: 'Plates',
    material: '9Cr–1Mo',
    spec: 'A387',
    grade: '9',
    cls: '1',
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.8, 14.1,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 60,
    form: 'Plates',
    material: '1¼Cr–½Mo–Si',
    spec: 'A387',
    grade: '11',
    cls: '1',
    uns: 'K11789',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 35,
    s: [
      20.0, 20.0, 20.0, 19.6, 18.9, 18.3, 18.0, 17.6, 17.2, 16.8, 16.4, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 61,
    form: 'Plates',
    material: '5Cr–½Mo',
    spec: 'A387',
    grade: '5',
    cls: '1',
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 62,
    form: 'Plates',
    material: '3Cr–1Mo',
    spec: 'A387',
    grade: '21',
    cls: '1',
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.3, 17.5, 17.0, 16.6, 16.2, 16.0, 15.8, 15.5, 15.2, 14.9, 12.0,
      9.0, 7.0, 5.5, 4.0, 2.7, 1.5,
    ],
  },
  {
    line: 63,
    form: 'Plates',
    material: '2¼Cr–1Mo',
    spec: 'A387',
    grade: '22',
    cls: '1',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 64,
    form: 'Plates',
    material: '2¼Ni',
    spec: 'A203',
    grade: 'A',
    cls: null,
    uns: 'K21703',
    pNo: '9A',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 65,
    yield: 37,
    s: [
      21.7, 21.7, 21.7, 21.1, 20.0, 18.5, 17.6, 16.6, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5,
    ],
  },
  {
    line: 65,
    form: 'Plates',
    material: '3½Ni',
    spec: 'A203',
    grade: 'D',
    cls: null,
    uns: 'K31718',
    pNo: '9B',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 65,
    yield: 40,
    s: [
      21.7, 21.7, 21.7, 21.1, 20.0, 18.5, 17.6, 16.6, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5,
    ],
  },
  {
    line: 66,
    form: 'Plates',
    material: 'C–½Mo',
    spec: 'A204',
    grade: 'A',
    cls: null,
    uns: 'K11820',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 65,
    yield: 37,
    s: [
      21.7, 21.7, 21.7, 21.5, 20.8, 20.2, 19.8, 19.4, 19.0, 18.4, 17.9, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 67,
    form: 'Plates',
    material: '1Cr–½Mo',
    spec: 'A387',
    grade: '12',
    cls: '2',
    uns: 'K11757',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 65,
    yield: 40,
    s: [
      21.7, 21.3, 20.8, 20.8, 20.8, 20.3, 20.0, 19.7, 19.4, 19.1, 18.6, 18.0,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 68,
    form: 'Plates',
    material: '2¼Ni',
    spec: 'A203',
    grade: 'B',
    cls: null,
    uns: 'K22103',
    pNo: '9A',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.8, 21.6, 20.0, 19.0, 16.9, 13.9, 11.4, 9.0, 6.5, 4.5,
      2.5,
    ],
  },
  {
    line: 69,
    form: 'Plates',
    material: '3½Ni',
    spec: 'A203',
    grade: 'E',
    cls: null,
    uns: 'K32018',
    pNo: '9B',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.8, 21.6, 20.0, 19.0, 18.0, 14.8, 12.0, 9.3, 6.5, 4.5,
      2.5,
    ],
  },
  {
    line: 70,
    form: 'Plates',
    material: '½Cr–½Mo',
    spec: 'A387',
    grade: '2',
    cls: '2',
    uns: 'K12143',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 70,
    yield: 45,
    s: [
      23.3, 23.3, 23.3, 23.3, 23.3, 23.3, 23.3, 23.3, 23.1, 22.4, 21.7, 20.9,
      9.2, 5.9,
    ],
  },
  {
    line: 71,
    form: 'Plates',
    material: 'C–½Mo',
    spec: 'A204',
    grade: 'B',
    cls: null,
    uns: 'K12020',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.2, 22.5, 21.8, 21.4, 21.0, 20.5, 19.9, 19.3, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 72,
    form: 'Plates',
    material: 'Mn–½Mo',
    spec: 'A302',
    grade: 'A',
    cls: null,
    uns: 'K12021',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 25.0, 25.0, 25.0, 25.0, 24.9, 24.4, 23.9, 23.2, 20.0, 13.7,
      8.2, 4.8,
    ],
  },
  {
    line: 73,
    form: 'Plates',
    material: 'C–½Mo',
    spec: 'A204',
    grade: 'C',
    cls: null,
    uns: 'K12320',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 75,
    yield: 43,
    s: [
      25.0, 25.0, 25.0, 25.0, 24.2, 23.4, 23.0, 22.6, 22.0, 21.4, 20.7, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 74,
    form: 'Plates',
    material: '1¼Cr–½Mo–Si',
    spec: 'A387',
    grade: '11',
    cls: '2',
    uns: 'K11789',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 25.0, 25.0, 24.4, 23.5, 23.1, 22.6, 22.2, 21.6, 20.2, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 75,
    form: 'Plates',
    material: '5Cr–½Mo',
    spec: 'A387',
    grade: '5',
    cls: '2',
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 24.9, 24.2, 24.0, 24.0, 23.6, 23.2, 22.7, 16.5, 16.0, 15.1, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 76,
    form: 'Plates',
    material: '3Cr–1Mo',
    spec: 'A387',
    grade: '21',
    cls: '2',
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 18.1, 13.1,
      9.5, 6.8, 4.9, 3.2, 2.4, 1.3,
    ],
  },
  {
    line: 77,
    form: 'Plates',
    material: '2¼Cr–1Mo',
    spec: 'A387',
    grade: '22',
    cls: '2',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 24.3, 24.1, 24.0, 23.8, 23.6, 23.4, 23.0, 22.5, 21.9, 15.8,
      11.4, 7.8, 5.1, 3.2, 2.0, 1.2,
    ],
  },
  {
    line: 78,
    form: 'Plates',
    material: 'Mn–½Mo',
    spec: 'A302',
    grade: 'B',
    cls: null,
    uns: 'K12022',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 80,
    yield: 50,
    s: [
      26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.5, 25.7, 20.0, 13.7,
      8.2, 4.8,
    ],
  },
  {
    line: 79,
    form: 'Plates',
    material: 'Mn–½Mo–½Ni',
    spec: 'A302',
    grade: 'C',
    cls: null,
    uns: 'K12039',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 80,
    yield: 50,
    s: [
      26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.5, 25.7, 20.0, 13.7,
      8.2, 4.8,
    ],
  },
  {
    line: 80,
    form: 'Plates',
    material: 'Mn–½Mo–¾Ni',
    spec: 'A302',
    grade: 'D',
    cls: null,
    uns: 'K12054',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 80,
    yield: 50,
    s: [
      26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.7, 26.5, 25.7, 20.0, 13.7,
      8.2, 4.8,
    ],
  },
  {
    line: 81,
    form: 'Plates',
    material: '9Cr–1Mo–V',
    spec: 'A387',
    grade: '91',
    cls: null,
    uns: 'K90901',
    pNo: '15E',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 60,
    s: [
      28.3, 28.3, 28.3, 28.2, 28.1, 27.7, 27.3, 26.7, 25.9, 24.9, 23.7, 22.3,
      20.7, 18.0, 14.0, 10.3, 7.0, 4.3,
    ],
  },
  {
    line: 82,
    form: 'Plates',
    material: '8Ni',
    spec: 'A553',
    grade: 'II',
    cls: null,
    uns: 'K71340',
    pNo: '11A',
    minTempF: -275,
    maxTempF: 100,
    tensile: 100,
    yield: 85,
    s: [33.3],
  },
  {
    line: 83,
    form: 'Plates',
    material: '5Ni–¼Mo',
    spec: 'A645',
    grade: 'A',
    cls: null,
    uns: 'K41583',
    pNo: '11A',
    minTempF: -275,
    maxTempF: 200,
    tensile: 95,
    yield: 65,
    s: [31.7, 31.7],
  },
  {
    line: 84,
    form: 'Plates',
    material: '9Ni',
    spec: 'A553',
    grade: 'I',
    cls: null,
    uns: 'K81340',
    pNo: '11A',
    minTempF: -320,
    maxTempF: 200,
    tensile: 100,
    yield: 85,
    s: [33.3, 33.3],
  },
  {
    line: 85,
    form: 'Plates',
    material: '9Ni',
    spec: 'A353',
    grade: 'I',
    cls: null,
    uns: 'K81340',
    pNo: '11A',
    minTempF: -320,
    maxTempF: 200,
    tensile: 100,
    yield: 75,
    s: [33.3, 33.3],
  },
  // ---- Forgings & Fittings (86–111) ----
  {
    line: 86,
    form: 'Forgings & Fittings',
    material: 'C–½Mo',
    spec: 'A234',
    grade: 'WP1',
    cls: null,
    uns: 'K12821',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 55,
    yield: 30,
    s: [
      18.3, 18.3, 18.0, 17.4, 16.9, 16.4, 16.1, 15.7, 15.4, 14.9, 14.5, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 87,
    form: 'Forgings & Fittings',
    material: '1Cr–½Mo',
    spec: 'A182',
    grade: 'F12',
    cls: '1',
    uns: 'K11562',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 32,
    s: [
      20.0, 19.3, 18.1, 17.3, 16.7, 16.3, 16.0, 15.8, 15.5, 15.3, 14.9, 14.5,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 88,
    form: 'Forgings & Fittings',
    material: '1Cr–½Mo',
    spec: 'A234',
    grade: 'WP12',
    cls: '1',
    uns: 'K12062',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 32,
    s: [
      20.0, 19.3, 18.1, 17.3, 16.7, 16.3, 16.0, 15.8, 15.5, 15.3, 14.9, 14.5,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 89,
    form: 'Forgings & Fittings',
    material: '1¼Cr–½Mo–Si',
    spec: 'A182',
    grade: 'F11',
    cls: '1',
    uns: 'K11597',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.5, 17.6, 16.8, 16.2, 15.7, 15.4, 15.1, 14.8, 14.4, 14.0, 13.6,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 90,
    form: 'Forgings & Fittings',
    material: '1¼Cr–½Mo–Si',
    spec: 'A234',
    grade: 'WP11',
    cls: '1',
    uns: 'K11597',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.5, 17.6, 16.8, 16.2, 15.7, 15.4, 15.1, 14.8, 14.4, 14.0, 13.6,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 91,
    form: 'Forgings & Fittings',
    material: '2¼Cr–1Mo',
    spec: 'A182',
    grade: 'F22',
    cls: '1',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 92,
    form: 'Forgings & Fittings',
    material: '2¼Cr–1Mo',
    spec: 'A234',
    grade: 'WP22',
    cls: '1',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.7, 18.2, 18.0, 17.9, 17.9, 17.9, 17.9, 17.9, 17.7, 17.1, 13.6,
      10.8, 8.0, 5.7, 3.8, 2.4, 1.4,
    ],
  },
  {
    line: 93,
    form: 'Forgings & Fittings',
    material: '5Cr–½Mo',
    spec: 'A234',
    grade: 'WP5',
    cls: null,
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 94,
    form: 'Forgings & Fittings',
    material: '9Cr–1Mo',
    spec: 'A234',
    grade: 'WP9',
    cls: null,
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 60,
    yield: 30,
    s: [
      20.0, 18.1, 17.4, 17.2, 17.1, 16.8, 16.6, 16.3, 15.9, 15.4, 14.8, 14.1,
      11.0, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 95,
    form: 'Forgings & Fittings',
    material: '3½Ni',
    spec: 'A420',
    grade: 'WPL3',
    cls: null,
    uns: 'K31918',
    pNo: '9B',
    minTempF: -150,
    maxTempF: 650,
    tensile: 65,
    yield: 35,
    s: [21.7, 21.4, 20.6, 19.9, 18.9, 17.5, 16.7],
  },
  {
    line: 96,
    form: 'Forgings & Fittings',
    material: '3½Ni',
    spec: 'A350',
    grade: 'LF3',
    cls: null,
    uns: 'K32025',
    pNo: '9B',
    minTempF: -150,
    maxTempF: 650,
    tensile: 70,
    yield: 37.5,
    s: [23.3, 22.9, 22.1, 21.4, 20.3, 18.8, 17.9],
  },
  {
    line: 97,
    form: 'Forgings & Fittings',
    material: '½Cr–½Mo',
    spec: 'A182',
    grade: 'F2',
    cls: null,
    uns: 'K12122',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.2, 22.5, 21.8, 21.4, 21.0, 20.5, 19.9, 19.3, 18.6,
      9.2, 5.9,
    ],
  },
  {
    line: 98,
    form: 'Forgings & Fittings',
    material: 'C–½Mo',
    spec: 'A182',
    grade: 'F1',
    cls: null,
    uns: 'K12822',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.2, 22.5, 21.8, 21.4, 21.0, 20.5, 19.9, 19.3, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 99,
    form: 'Forgings & Fittings',
    material: '1Cr–½Mo',
    spec: 'A182',
    grade: 'F12',
    cls: '2',
    uns: 'K11564',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 22.9, 22.4, 21.7, 20.9, 20.3, 20.0, 19.7, 19.4, 19.1, 18.6, 18.0,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 100,
    form: 'Forgings & Fittings',
    material: '1Cr–½Mo',
    spec: 'A234',
    grade: 'WP12',
    cls: '2',
    uns: 'K12062',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 22.9, 22.4, 21.7, 20.9, 20.3, 20.0, 19.7, 19.4, 19.1, 18.6, 18.0,
      11.3, 7.2, 4.5, 2.8, 1.8, 1.1,
    ],
  },
  {
    line: 101,
    form: 'Forgings & Fittings',
    material: '1¼Cr–½Mo–Si',
    spec: 'A182',
    grade: 'F11',
    cls: '2',
    uns: 'K11572',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.5, 21.7, 20.9, 20.5, 20.1, 19.7, 19.2, 18.7, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 102,
    form: 'Forgings & Fittings',
    material: '1¼Cr–½Mo–Si',
    spec: 'A234',
    grade: 'WP11',
    cls: '2',
    uns: 'K11572',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.5, 21.7, 20.9, 20.5, 20.1, 19.7, 19.2, 18.7, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 103,
    form: 'Forgings & Fittings',
    material: '5Cr–½Mo',
    spec: 'A182',
    grade: 'F5',
    cls: null,
    uns: 'K41545',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 22.6, 22.4, 22.4, 22.0, 21.7, 21.2, 20.6, 19.8, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 104,
    form: 'Forgings & Fittings',
    material: '3Cr–1Mo',
    spec: 'A182',
    grade: 'F21',
    cls: null,
    uns: 'K31545',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 24.3, 24.1, 24.0, 23.8, 23.6, 23.4, 23.0, 22.5, 18.1, 13.1,
      9.5, 6.8, 4.9, 3.2, 2.4, 1.3,
    ],
  },
  {
    line: 105,
    form: 'Forgings & Fittings',
    material: '2¼Cr–1Mo',
    spec: 'A182',
    grade: 'F22',
    cls: '3',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 21.9, 15.8,
      11.4, 7.8, 5.1, 3.2, 2.0, 1.2,
    ],
  },
  {
    line: 106,
    form: 'Forgings & Fittings',
    material: '2¼Cr–1Mo',
    spec: 'A234',
    grade: 'WP22',
    cls: '3',
    uns: 'K21590',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 75,
    yield: 45,
    s: [
      25.0, 25.0, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 24.2, 21.9, 15.8,
      11.4, 7.8, 5.1, 3.2, 2.0, 1.2,
    ],
  },
  {
    line: 107,
    form: 'Forgings & Fittings',
    material: '9Cr–1Mo',
    spec: 'A182',
    grade: 'F9',
    cls: null,
    uns: 'K90941',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 55,
    s: [
      28.3, 28.3, 27.4, 27.2, 27.1, 26.8, 26.3, 25.8, 25.0, 24.0, 22.9, 15.2,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
  {
    line: 108,
    form: 'Forgings & Fittings',
    material: '9Cr–1Mo–V',
    spec: 'A182',
    grade: 'F91',
    cls: null,
    uns: 'K90901',
    pNo: '15E',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 60,
    s: [
      28.3, 28.3, 28.3, 28.2, 28.1, 27.7, 27.3, 26.7, 25.9, 24.9, 23.7, 22.3,
      20.7, 18.0, 14.0, 10.3, 7.0, 4.3,
    ],
  },
  {
    line: 109,
    form: 'Forgings & Fittings',
    material: '9Cr–1Mo–V',
    spec: 'A234',
    grade: 'WP91',
    cls: null,
    uns: 'K90901',
    pNo: '15E',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 85,
    yield: 60,
    s: [
      28.3, 28.3, 28.3, 28.2, 28.1, 27.7, 27.3, 26.7, 25.9, 24.9, 23.7, 22.3,
      20.7, 18.0, 14.0, 10.3, 7.0, 4.3,
    ],
  },
  {
    line: 110,
    form: 'Forgings & Fittings',
    material: '5Cr–½Mo',
    spec: 'A182',
    grade: 'F5a',
    cls: null,
    uns: 'K42544',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 90,
    yield: 65,
    s: [
      30.0, 29.9, 29.1, 28.8, 28.7, 28.3, 27.9, 27.3, 26.5, 25.5, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 111,
    form: 'Forgings & Fittings',
    material: '9Ni',
    spec: 'A420',
    grade: 'WPL8',
    cls: null,
    uns: 'K81340',
    pNo: '11A',
    minTempF: -320,
    maxTempF: 200,
    tensile: 100,
    yield: 75,
    s: [33.3, 33.3],
  },
  // ---- Castings (112–121) ----
  {
    line: 112,
    form: 'Castings',
    material: 'C–½Mo',
    spec: 'A352',
    grade: 'LC1',
    cls: null,
    uns: 'J12522',
    pNo: '3',
    minTempF: -75,
    maxTempF: 700,
    tensile: 65,
    yield: 35,
    s: [21.7, 21.7, 21.0, 20.3, 19.7, 19.1, 18.7, 18.4],
  },
  {
    line: 113,
    form: 'Castings',
    material: 'C–½Mo',
    spec: 'A217',
    grade: 'WC1',
    cls: null,
    uns: 'J12524',
    pNo: '3',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 65,
    yield: 35,
    s: [
      21.7, 21.7, 21.0, 20.3, 19.7, 19.1, 18.7, 18.4, 17.9, 17.4, 16.9, 13.7,
      8.2, 4.8, 4.0, 2.4,
    ],
  },
  {
    line: 114,
    form: 'Castings',
    material: '2½Ni',
    spec: 'A352',
    grade: 'LC2',
    cls: null,
    uns: 'J22500',
    pNo: '9A',
    minTempF: -100,
    maxTempF: 650,
    tensile: 70,
    yield: 40,
    s: [23.3, 23.3, 23.3, 22.8, 21.6, 20.0, 19.0],
  },
  {
    line: 115,
    form: 'Castings',
    material: '3½Ni',
    spec: 'A352',
    grade: 'LC3',
    cls: null,
    uns: 'J31550',
    pNo: '9B',
    minTempF: -150,
    maxTempF: 650,
    tensile: 70,
    yield: 40,
    s: [23.3, 23.3, 23.3, 22.8, 21.6, 20.0, 19.0],
  },
  {
    line: 116,
    form: 'Castings',
    material: '1Ni–½Cr–½Mo',
    spec: 'A217',
    grade: 'WC4',
    cls: null,
    uns: 'J12082',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1000,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.3, 23.0, 22.4, 22.1, 21.7, 21.2, 20.6, 19.8, 14.3,
      9.2, 5.9,
    ],
  },
  {
    line: 117,
    form: 'Castings',
    material: '¾Ni–1Mo–¾Cr',
    spec: 'A217',
    grade: 'WC5',
    cls: null,
    uns: 'J22000',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1100,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 23.3, 23.0, 22.4, 22.1, 21.7, 21.2, 20.6, 19.8, 14.3,
      9.2, 5.9, 4.0, 2.4,
    ],
  },
  {
    line: 118,
    form: 'Castings',
    material: '1¼Cr–½Mo',
    spec: 'A217',
    grade: 'WC6',
    cls: null,
    uns: 'J12072',
    pNo: '4',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 23.3, 22.5, 21.7, 20.9, 20.5, 20.1, 19.7, 19.2, 18.7, 13.7,
      9.3, 6.3, 4.2, 2.8, 1.9, 1.2,
    ],
  },
  {
    line: 119,
    form: 'Castings',
    material: '2¼Cr–1Mo',
    spec: 'A217',
    grade: 'WC9',
    cls: null,
    uns: 'J21890',
    pNo: '5A',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 70,
    yield: 40,
    s: [
      23.3, 23.3, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 22.6, 21.9, 15.8,
      11.4, 7.8, 5.1, 3.2, 2.0, 1.2,
    ],
  },
  {
    line: 120,
    form: 'Castings',
    material: '5Cr–½Mo',
    spec: 'A217',
    grade: 'C5',
    cls: null,
    uns: 'J42045',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 90,
    yield: 60,
    s: [
      30.0, 29.9, 29.1, 28.8, 28.7, 28.3, 27.9, 27.3, 26.5, 25.5, 14.3, 10.9,
      8.0, 5.8, 4.2, 2.9, 1.8, 1.0,
    ],
  },
  {
    line: 121,
    form: 'Castings',
    material: '9Cr–1Mo',
    spec: 'A217',
    grade: 'C12',
    cls: null,
    uns: 'J82090',
    pNo: '5B',
    minTempF: -20,
    maxTempF: 1200,
    tensile: 90,
    yield: 60,
    s: [
      30.0, 29.9, 29.1, 28.8, 28.7, 28.3, 27.9, 27.3, 26.5, 25.5, 24.2, 15.2,
      10.6, 7.4, 5.0, 3.3, 2.2, 1.5,
    ],
  },
] as const satisfies readonly RawRow[]
